/**
 * Unified Call Orchestrator — single entry point for ALL calls (smart_queue, dialer, button, callback).
 * Replaces direct calls to smartQueueService.dialContact and crmService.createCall.
 */

import { api } from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export type CallOrigin = 'smart_queue' | 'dialer' | 'button' | 'callback';

export interface StartCallParams {
  phone: string;
  origin: CallOrigin;
  contactId?: string;
  opportunityId?: string;
  sessionId?: string;
  language?: string;
}

export interface UnifiedCallResponse {
  call_id: string;
  call_control_id: string | null;
  contact_id: string | null;
  opportunity_id: string | null;
  origin: string;
  status: string;
  phone: string;
  stream_channels: { call?: string; control?: string };
  is_new_contact: boolean;
}

/**
 * Next follow-up to schedule when completing a call.
 *
 * Mirrors `app/schemas/call_orchestrator.py::NextFollowUpSpec`. Sent
 * as `next_follow_up` inside the CallResultPayload. Backend creates a
 * FollowUp v2 (state=PLANNED) and emits an activity-log row in the
 * same transaction (R2).
 */
export interface NextFollowUpPayload {
  next_action_type: 'call' | 'whatsapp' | 'email' | 'meeting' | 'reminder';
  owner_id: string; // uuid; R1 — required
  sla_deadline: string; // ISO-8601 with tz
  coverage_user_id?: string | null;
  notes?: string | null;
}

export interface CallResultPayload {
  call_result: string;
  call_type?: string | null;
  notes?: string | null;
  transcription?: string | null;
  questionnaire?: Record<string, unknown> | null;
  follow_up_date?: string | null;
  grading_llamada?: string | null;
  grading_situacion?: string | null;
  servicio_propuesto?: string | null;
  /** Sprint 6 (follow-ups v2) — próximo seguimiento programado desde el form. */
  next_follow_up?: NextFollowUpPayload | null;
}

export interface ResolvedContact {
  contact_id: string;
  contact_name: string;
  phone: string;
  opportunity_id: string | null;
  is_new: boolean;
}

// ── API Functions ────────────────────────────────────────────────────────────

/**
 * Start a call via the unified orchestrator.
 * Backend resolves contact/opportunity automatically if not provided.
 */
export async function startCall(params: StartCallParams): Promise<UnifiedCallResponse> {
  const { data } = await api.post<UnifiedCallResponse>('/crm/calls/start', {
    phone: params.phone,
    origin: params.origin,
    contact_id: params.contactId ?? null,
    opportunity_id: params.opportunityId ?? null,
    session_id: params.sessionId ?? null,
    language: params.language ?? 'es',
  });
  return data;
}

/**
 * Complete a call with result, notes, and questionnaire.
 */
export async function completeCall(callId: string, payload: CallResultPayload): Promise<void> {
  await api.post(`/crm/calls/${callId}/complete`, payload);
}

/**
 * Find a contact by phone number (exact match).
 */
export async function findContactByPhone(phone: string): Promise<ResolvedContact | null> {
  const { data } = await api.get<ResolvedContact | null>('/crm/contacts/by-phone', {
    params: { phone },
  });
  return data;
}

/**
 * Fuzzy search for contacts by phone (partial match, up to 5 results).
 */
export async function findContactByPhoneFuzzy(phone: string): Promise<ResolvedContact[]> {
  const { data } = await api.get<ResolvedContact[]>('/crm/contacts/by-phone/suggestions', {
    params: { phone },
  });
  return data;
}

/**
 * Auto-save gathering data during an active call (P4).
 * Called every 30s — idempotent merge on backend.
 */
export async function saveGatheringData(
  callId: string,
  gathering: Record<string, unknown>,
): Promise<void> {
  await api.patch(`/crm/calls/${callId}/gathering`, gathering);
}
